import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/data/translations';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { X, FileText, Download } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrochureModal({ open, onOpenChange }) {
  const { lang } = useLanguage();
  const txt = t.brochureModal[lang];
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [documents, setDocuments] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/brochure-request`, { email, name });
      if (res.data.success) {
        setSuccess(true);
        setDocuments(res.data.documents || []);
        // Auto-open main brochure
        if (res.data.brochure_url) {
          setTimeout(() => window.open(res.data.brochure_url, '_blank'), 500);
        }
      }
    } catch {
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSuccess(false);
      setDocuments([]);
      setEmail('');
      setName('');
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="brochure-modal"
        className="bg-obsidian border border-white/10 text-alpine-mist max-w-md p-8 sm:p-10"
      >
        <button
          data-testid="brochure-modal-close"
          onClick={handleClose}
          className="absolute top-4 right-4 text-alpine-mist/40 hover:text-alpine-mist transition-colors"
        >
          <X size={18} />
        </button>
        <DialogHeader className="space-y-3">
          <DialogTitle className="font-serif text-2xl tracking-tight">{txt.title}</DialogTitle>
          <DialogDescription className="text-alpine-mist/50 text-sm leading-relaxed font-sans">
            {success ? (lang === 'fr' ? 'Vos documents sont prêts' : 'Your documents are ready') : txt.subtitle}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="mt-6 space-y-3" data-testid="brochure-success">
            <p className="text-gold-muted text-sm font-sans mb-4">{txt.success}</p>
            
            <div className="space-y-2">
              {documents.map((doc) => (
                <a
                  key={doc.id}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`brochure-doc-${doc.id}`}
                  className="flex items-center gap-3 p-3 border border-white/10 rounded hover:border-gold-muted/40 hover:bg-white/5 transition-all group"
                >
                  <FileText size={18} className="text-gold-muted/60 shrink-0" />
                  <span className="text-sm text-alpine-mist/70 group-hover:text-alpine-mist flex-1 font-sans">
                    {lang === 'fr' ? doc.name_fr : doc.name_en}
                  </span>
                  <Download size={14} className="text-alpine-mist/30 group-hover:text-gold-muted transition-colors" />
                </a>
              ))}
            </div>

            <button
              onClick={handleClose}
              className="cta-luxury w-full text-center mt-6"
              data-testid="brochure-close-btn"
            >
              {lang === 'fr' ? 'Fermer' : 'Close'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="brochure-form">
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={txt.namePlaceholder}
              className="input-luxury"
              data-testid="brochure-name-input"
            />
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={txt.placeholder}
              required
              className="input-luxury"
              data-testid="brochure-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="brochure-submit-btn"
              className="cta-luxury w-full text-center mt-4"
            >
              {loading ? '...' : txt.cta}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
